import { z } from "zod";
import { MATERIAL_RETENTION_DAYS } from "./materials.ts";
import { MediaFile } from "./media.ts";
import { PlanSummary } from "./plans.ts";
import { EXTERNAL_CALL_URL_MAX } from "./schedule.ts";

/**
 * Материал домашки: текст задания и картинки рядом с заданиями. Проект целиком:
 * `docs/homework-scratch-design.md` (редакция 3).
 *
 * Отдельным файлом от `./homework.ts` по двум причинам. Первая механическая:
 * тот давно за ориентиром в 400 строк. Вторая содержательная: здесь живут
 * потолки и формы, которые обязаны одинаково знать обе стороны (форма выдачи и
 * ручка создания), а в `./homework.ts` остаётся автомат состояний.
 *
 * ⚠️ Этот модуль НЕ импортирует `./homework.ts`: наоборот, тот берёт отсюда.
 * Цикл между ними дал бы `undefined` на месте схемы при первом же разборе.
 */

/**
 * Потолок текста задания в знаках (1.1 проекта, просьба владельца 26.09.2026).
 *
 * Это БОЛЬШОЕ поле, а не строка пометки: туда вставляют условие задачи, абзац
 * для чтения, список заданий из учебника. Десять тысяч это около четырёх
 * страниц. Проверяется схемой с обеих сторон, в базе тем же числом стоит CHECK.
 */
export const HOMEWORK_TASK_TEXT_MAX = 10_000;

/**
 * Сколько картинок можно приложить к одной домашке (1.1 проекта).
 *
 * ⚠️ Это НЕ `HOMEWORK_MAX_EXERCISES`: тот про задания, и отказ у него свой
 * (`homework_full`). Потолок картинок технический, он стоит в схеме тела.
 */
export const HOMEWORK_MATERIAL_MAX = 20;

/**
 * Сколько загрузок картинок материала в минуту принимаем от одной сессии.
 * Тот же механизм, что у фото решения (`HOMEWORK_PHOTO_RATE_MAX`), и тот же
 * принцип: считается ОТ потолка и строго больше него, иначе законное «выбрал
 * разом все двадцать и одну перезалил после обрыва» получало бы «слишком
 * быстро», то есть отказ, который врёт про причину.
 */
export const HOMEWORK_MATERIAL_RATE_MAX = HOMEWORK_MATERIAL_MAX * 2;

/**
 * Текст задания как он приезжает. Переносы строк и пустые строки сохраняются
 * как есть (у ученика `white-space: pre-wrap`), поэтому схема НЕ режет пробелы:
 * `trim` съел бы отступы, которые человек поставил намеренно. Пустой по
 * существу текст (одни пробелы) сервер записывает как его отсутствие.
 */
export const HomeworkTaskText = z
  .string()
  .max(HOMEWORK_TASK_TEXT_MAX)
  .refine(noNulChar, { message: "символ U+0000 в тексте" });

/**
 * Нет символа U+0000. ⚠️ Postgres не хранит его в `text` вовсе («invalid byte
 * sequence for encoding UTF8: 0x00»), то есть без этой проверки вставленный из
 * чужого редактора мусор давал бы 500 вместо внятного отказа по телу.
 * Проверено на базе 26.09.2026. Общая с ответом ученика (`homework-answer.ts`).
 */
export function noNulChar(text: string): boolean {
  return !text.includes("\u0000");
}

/**
 * Файлы картинок в порядке показа. Повтор одного файла это ошибка клиента, а не
 * «показать дважды»: ключ связи `(домашка, файл)`, и молча схлопнутый повтор
 * означал бы ленту короче, чем человек видел в форме.
 */
export const HomeworkMaterialFileIds = z
  .array(z.string().uuid())
  .max(HOMEWORK_MATERIAL_MAX)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: "одна и та же картинка встречается дважды",
  });

/**
 * Картинка материала в карточке домашки (1.3 проекта).
 *
 * ⚠️ **Ссылка на содержимое приезжает ПОДПИСАННОЙ прямо здесь**, а не отдельным
 * `POST /v1/media/files/:fileId/link` на каждую картинку: двадцать кругов на
 * телефоне это двадцать отдельных задержек. Подпись живёт минуты
 * (`expiresAt`), и просроченную клиент лечит перечитыванием карточки.
 *
 * `width`/`height` нужны, чтобы коробка картинки знала размер заранее и лента
 * не прыгала при ленивой загрузке. NULL означает «браузер не сообщил», и тогда
 * клиент берёт запасное 4:3.
 */
export const HomeworkMaterialImage = z.object({
  fileId: z.string().uuid(),
  position: z.number().int().nonnegative(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  /**
   * Подписанный путь к содержимому. `null` только у файла, который всё же
   * оказался удалён: удаление из-под домашки запрещено (1.6), но показать
   * пустую коробку честнее, чем уронить карточку.
   */
  path: z.string().nullable(),
  expiresAt: z.number().int().nullable(),
});
export type HomeworkMaterialImage = z.infer<typeof HomeworkMaterialImage>;

/**
 * Аудио или видео материала в карточке домашки (`docs/homework-media-plan.md`,
 * Р5). Строки той же `homework_material`, что у картинок (Р1), и позиция из той
 * же шкалы: клиент сливает `material` и `media` по `position`.
 *
 * ⚠️ **Отдельным полем карточки, а не видом внутри `material`.** Старый бандл в
 * открытой вкладке переживает выкатку и разобрал бы аудио схемой картинки:
 * `<img>` на MP3, отказ, «Картинка не загрузилась». Отдельное поле он просто не
 * видит.
 */
export const HomeworkMaterialMedia = z.object({
  fileId: z.string().uuid(),
  position: z.number().int().nonnegative(),
  kind: z.enum(["audio", "video"]),
  contentType: z.string(),
  /** Длительность, если её сообщил браузер при загрузке. NULL: неизвестна. */
  durationMs: z.number().int().nonnegative().nullable(),
  /** ⚠️ В базе bigint, драйвер отдаёт строкой: разбор в сборке карточки. */
  sizeBytes: z.number().int().nonnegative(),
  /** Подписанный путь к содержимому. `null` только у всё же удалённого файла. */
  path: z.string().nullable(),
  expiresAt: z.number().int().nullable(),
  /** Кадр для `poster` у видео, если превью снято. Подпись своя, `preview`. */
  posterPath: z.string().nullable(),
  /**
   * Имя файла. ⚠️ **Только в карточке преподавателя** (нужно «Задать ещё раз»);
   * ученику ключа нет вовсе. Это не значит, что имя до ученика не доходит:
   * отдача содержимого ставит его в `Content-Disposition` (Р5 плана).
   */
  name: z.string().optional(),
});
export type HomeworkMaterialMedia = z.infer<typeof HomeworkMaterialMedia>;

/**
 * Скорость канала, под которую считается «тяжёлый» файл, в кбит/с. Цифра
 * раздела 3 CLAUDE.md: столько остаётся на мобильной сети с ограничениями.
 */
export const HOMEWORK_SLOW_LINK_KBPS = 128;

/**
 * Сколько секунд ожидания СВЕРХ самой записи ещё не тяжело. ⚠️ Число решения,
 * а не замера (Р3 плана): минута догрузки на запись терпима, больше уже нет.
 */
export const HOMEWORK_MEDIA_EXTRA_WAIT_SEC = 60;

/**
 * Потолок полной загрузки, когда длительность неизвестна и сравнивать не с чем.
 * ⚠️ Тоже число решения: дольше десяти минут ученик домашку не ждёт. На
 * 128 кбит/с это 9,6 МБ.
 */
export const HOMEWORK_MEDIA_FULL_WAIT_SEC = 600;

/**
 * Тяжёлый ли файл для ученика на медленной сети (Р3 плана). Одно правило на
 * аудио и видео и на обе стороны: предупреждение в форме выдачи и строка про
 * Wi‑Fi у ученика.
 *
 * ⚠️ **Мерится ОЖИДАНИЕ, а не битрейт**: предупреждение обещает «ученик может не
 * дождаться». Короткий MP3 320 кбит/с битрейтом пугает, а ждут его сверх записи
 * полминуты. Теги и обложка поглощаются формулой без отдельного запаса.
 */
export function heavyMedia(args: { sizeBytes: number; durationMs: number | null }): boolean {
  const loadSec = (args.sizeBytes * 8) / (HOMEWORK_SLOW_LINK_KBPS * 1000);
  if (args.durationMs === null) return loadSec > HOMEWORK_MEDIA_FULL_WAIT_SEC;
  return loadSec - args.durationMs / 1000 > HOMEWORK_MEDIA_EXTRA_WAIT_SEC;
}

/** Ответ загрузки картинки материала (`POST /v1/homework/material-image`). */
export const HomeworkMaterialUploadResponse = z.object({ file: MediaFile });
export type HomeworkMaterialUploadResponse = z.infer<typeof HomeworkMaterialUploadResponse>;

/**
 * Откуда картинка в ленте урока (1.2 проекта): брошенная на доску или фон
 * слайда. Первые идут сверху, вторые ниже свёрнутым блоком: у импортированного
 * учебника фонов десятки, и среди них страницы ответов.
 */
export const SourceImageOrigin = z.enum(["board", "slide"]);
export type SourceImageOrigin = z.infer<typeof SourceImageOrigin>;

/**
 * Картинка, которую можно взять в материал: из доски урока или из заготовки.
 *
 * Ссылки подписаны здесь же по той же причине, что в карточке: лента это
 * десятки миниатюр, и круг на каждую был бы заметен даже у преподавателя.
 */
export const SourceImage = z.object({
  fileId: z.string().uuid(),
  name: z.string(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  source: SourceImageOrigin,
  path: z.string(),
  /** Кадр для плитки, если он снят. `null` означает «показывать сам файл». */
  previewPath: z.string().nullable(),
  expiresAt: z.number().int(),
  /**
   * Вес и тип файла. Нужны панели «Текст с картинки»
   * (`docs/handwriting-recognition-design.md`, решение 5): без них она не может
   * ни погасить кнопку у кадра, который ручка всё равно отвергнет, ни объяснить
   * причину словами ДО нажатия, то есть человек узнавал бы отказ после ожидания.
   *
   * ⚠️⚠️ **Оба поля НЕОБЯЗАТЕЛЬНЫЕ, и это требование, а не стиль.** Схема эта
   * общая у ОБЕИХ ручек списка кадров (урока и заготовки), а собирает строку
   * одна функция на обе (`toSourceImage`). Обязательное поле уронило бы разбор
   * ответа у той ручки, которая его не отдаёт, то есть источник «С доски урока»
   * в форме выдачи домашки. ⚠️ Typecheck этого не увидит: сырая строка приезжает
   * из `sql<SourceImageRow>`, а это утверждение типа, а не проверка. Краснеет
   * только `pnpm test:db`.
   */
  sizeBytes: z.number().int().nonnegative().optional(),
  contentType: z.string().optional(),
});
export type SourceImage = z.infer<typeof SourceImage>;

/** Ответ `GET /v1/lessons/:lessonId/images` и `GET /v1/plans/:planId/images`. */
export const SourceImagesResponse = z.object({ images: z.array(SourceImage) });
export type SourceImagesResponse = z.infer<typeof SourceImagesResponse>;

/**
 * Ответ «Сделал(а)» и его отмены (1.5 проекта).
 *
 * ⚠️ Отмена отвечает ТЕКУЩИМ значением, а не «снято»: после срока или при
 * приложенном фото отметка окончательна, и тогда ручка возвращает прежний
 * момент. Экран рисуется от числа, а не от того, что он просил.
 */
export const HomeworkDoneResponse = z.object({
  studentDoneAt: z.number().int().nullable(),
});
export type HomeworkDoneResponse = z.infer<typeof HomeworkDoneResponse>;

/**
 * Потолок адреса ссылки, у которого ведётся отметка нажатия
 * (`docs/homework-media-plan.md`, Р7). Тот же потолок, что у адреса выданного
 * материала и ссылки на созвон; в базе его держит CHECK
 * `homework_link_open_href_len` тем же числом. Длиннее: сервер отвечает 204 без
 * записи, клиент такой адрес не шлёт. Длина сравнивается после `new URL().href`.
 */
export const HOMEWORK_LINK_HREF_MAX = EXTERNAL_CALL_URL_MAX;

/**
 * Срок жизни отметок нажатия, в сутках, ОТ СОЗДАНИЯ домашки (Р8). Тот же довод,
 * что у квитанции материалов (отметка о действии ребёнка, цель исчерпывается за
 * учебный год), поэтому и число берётся оттуда: срок меняется вместе с ним.
 */
export const HOMEWORK_LINK_OPEN_RETENTION_DAYS = MATERIAL_RETENTION_DAYS;

/**
 * Тело отметки нажатия на ссылку текста задания (Р9). Адрес любой длины в
 * пределах того, что может дать допустимый текст: `%`-кодирование превращает
 * знак UTF-16 не больше чем в шесть, поэтому потолок `HOMEWORK_TASK_TEXT_MAX * 6`
 * (Р7), и адрес из любого текста задания отказа по длине не получает.
 */
export const HomeworkLinkOpenedRequest = z.object({
  href: z.string().min(1).max(HOMEWORK_TASK_TEXT_MAX * 6),
});
export type HomeworkLinkOpenedRequest = z.infer<typeof HomeworkLinkOpenedRequest>;

/**
 * Первое нажатие на ссылку текста задания (Р8, Р10). ⚠️ Только в карточке
 * преподавателя; ученику ключа `linkOpens` нет вовсе.
 */
export const HomeworkLinkOpen = z.object({
  href: z.string(),
  firstOpenedAt: z.number().int(),
});
export type HomeworkLinkOpen = z.infer<typeof HomeworkLinkOpen>;

/** Ответ «Сохранить картинки как заготовку» (1.7 проекта). */
export const HomeworkSaveAsPlanResponse = z.object({ plan: PlanSummary });
export type HomeworkSaveAsPlanResponse = z.infer<typeof HomeworkSaveAsPlanResponse>;

/** Название домашки без источника заданий (1.4 проекта): `title` NOT NULL. */
export const HOMEWORK_DEFAULT_TITLE = "Домашка";

/**
 * «Сделано после срока» у домашки, выполненность которой считает отметка
 * ученика (1.5 проекта). Показывается преподавателю, как пометка у попыток.
 * Строгое сравнение, как у `attemptMarks`: ровно в срок это ещё в срок.
 */
export function studentDoneAfterDue(args: {
  studentDoneAt: number | null;
  dueAt: number | null;
}): boolean {
  return args.studentDoneAt !== null && args.dueAt !== null && args.studentDoneAt > args.dueAt;
}
